import React, { useEffect, useState } from 'react';
import useAxios from '../utils/useAxios';
import { FaDownload, FaSignInAlt, FaSignOutAlt, FaBusinessTime, FaBriefcase, FaInfoCircle } from 'react-icons/fa';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import {
  FiAlertTriangle,
  FiSunrise,
  FiSunset,
  FiArrowDownCircle,
  FiCoffee,
} from 'react-icons/fi';

const STATUT_LABELS = {
  PRESENT: 'Présent',
  RETARD: 'Retard',
  ABSENT: 'Absent',
};

const PERIODES_FRONT = [
  { value: 'matin', label: 'Matin' },
  { value: 'apres_midi', label: 'Après-midi' },
  { value: 'pause', label: 'Pause' },
  { value: 'descente', label: 'Descente' },
  { value: 'exceptionnelle', label: 'Exceptionnelle' }
];

const TYPES_POINTAGE = [
  { value: 'entree', label: 'Entrée' },
  { value: 'sortie', label: 'Sortie' }
];

const MesPresences = () => {
  const api = useAxios();
  const [filters, setFilters] = useState({
    date: '',
    periode: '',
    statut: '',
    type_pointage: '',
  });
  const [presences, setPresences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ 
    totalPages: 1,
    count: 0,
    next: null,
    previous: null
  });
  const [page, setPage] = useState(1);
  const [hoursWorked, setHoursWorked] = useState({
    today: 0,
    week: 0,
    month: 0
  });
  const [montantPenalite, setMontantPenalite] = useState(0);
  
  const [motifModal, setMotifModal] = useState({ show: false, motif: '' });

  const generateMonths = () => {
    const months = [];
    const date = new Date();
    for (let i = 0; i < 12; i++) {
      const monthDate = new Date(date.getFullYear(), date.getMonth() - i, 1);
      const value = `${monthDate.getFullYear()}-${String(monthDate.getMonth() + 1).padStart(2, '0')}`;
      const label = monthDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
      months.push({ value, label });
    }
    return months;
  };

  const monthsList = generateMonths();
  const [selectedMonth, setSelectedMonth] = useState('');

  const formatHeuresCompletes = (hours) => {
    if (hours === undefined || hours === null) return '0h 00min';
    
    const heures = Math.floor(hours);
    const minutes = Math.round((hours - heures) * 60);
    return `${heures}h ${minutes.toString().padStart(2, '0')}min`;
  };

  const formatHeure = (heure) => {
    if (!heure) return '-';
    return heure.slice(0, 5); 
  };

  const getBackendPeriodeValue = (frontValue) => {
    return frontValue === 'exceptionnelle' ? 'autre' : frontValue;
  };

  const getFrontendPeriodeValue = (backendValue) => {
    return backendValue === 'autre' ? 'exceptionnelle' : backendValue;
  };

  const fetchPresences = async (targetPage = 1) => {
    setLoading(true);
    try {
      const params = {
        ...filters,
        periode: filters.periode ? getBackendPeriodeValue(filters.periode) : '',
        page: targetPage,
        ...(selectedMonth && { month: selectedMonth }),
      };
      
      Object.keys(params).forEach(key => {
        if (params[key] === '') delete params[key];
      });

      const response = await api.get('/pointage/mes-presences/', { params });

      if (response.data && response.data.results) {
        const results = response.data.results;
        setPresences(results.presences || []);
        setMontantPenalite(results.montant_penalite || 0);
        setPagination({
          totalPages: Math.ceil(response.data.count / 10) || 1,
          count: response.data.count || 0,
          next: response.data.next || null,
          previous: response.data.previous || null
        });
        setPage(targetPage);
      }
    } catch (error) {
      console.error("Erreur lors du chargement des présences :", error);
      setPresences([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchHoursWorked = async () => {
    try {
      const response = await api.get('/pointage/heures-travaillees/', {
        params: {
          ...(selectedMonth && { month: selectedMonth }),
        }
      });
      setHoursWorked({
        today: response.data.jour || 0,
        week: response.data.semaine || 0,
        month: response.data.mois || 0
      });
    } catch (error) {
      console.error("Erreur lors du chargement des heures travaillées :", error);
    }
  };

  useEffect(() => {
    fetchPresences(1);
    fetchHoursWorked();
  }, [filters, selectedMonth]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchPresences(newPage);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const getStatutColor = (statut) => {
    switch(statut) {
      case 'PRESENT': return 'bg-success';
      case 'RETARD': return 'bg-warning';
      case 'ABSENT': return 'bg-danger';
      default: return 'bg-secondary';
    }
  };

  const formatPeriode = (periode) => {
    const found = PERIODES_FRONT.find(p => 
      p.value === getFrontendPeriodeValue(periode)
    );
    return found ? found.label : '-';
  };

 
  const openMotifModal = (motif) => {
    setMotifModal({
      show: true,
      motif: motif || 'Aucun motif spécifié',
    });
  };


  const closeMotifModal = () => {
    setMotifModal({ show: false, motif: '' });
  };

  const handleExportPDF = async () => {
    setLoading(true);
    try {
      const doc = new jsPDF();
      doc.setFontSize(18);
      doc.text("Rapport de mes présences", 14, 20);

      if (selectedMonth) {
        const monthLabel = monthsList.find(m => m.value === selectedMonth)?.label;
        doc.setFontSize(12);
        doc.text(`Mois: ${monthLabel}`, 14, 30);
        doc.text(`Heures travaillées: ${formatHeuresCompletes(hoursWorked.month)}`, 14, 40);
        doc.text(`Pénalités: ${montantPenalite.toLocaleString()} FCFA`, 14, 50);
      }

      let allPresences = [];
      let currentPage = 1;
      let hasMore = true;

      while (hasMore) {
        const params = {
          ...filters,
          periode: filters.periode ? getBackendPeriodeValue(filters.periode) : '',
          page: currentPage,
          ...(selectedMonth && { month: selectedMonth }),
        };
        
        Object.keys(params).forEach(key => {
          if (params[key] === '') delete params[key];
        });

        const response = await api.get('/pointage/mes-presences/', { params });
        
        if (response.data && response.data.results) {
          allPresences = [...allPresences, ...response.data.results.presences];
          hasMore = !!response.data.next;
          currentPage++;
        } else {
          hasMore = false;
        }
      }


      const tableColumn = ["Date", "Heure", "Type", "Période", "Statut", "Justifié"];
      const tableRows = [];

      allPresences.forEach((p) => {
        tableRows.push([
          p.date || '-',
          p.heure || '-',
          p.type_pointage === "entree" ? "Entrée" : "Sortie",
          formatPeriode(p.periode),
          p.statut ? STATUT_LABELS[p.statut] || p.statut : '-',
          
          (p.statut === "RETARD" || p.statut === "ABSENT") 
            ? (p.est_justifiee ? "Oui" : "Non") 
            : '-',
          
        ]);
      });

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: selectedMonth ? 60 : 30,
        styles: { fontSize: 10 },
        headStyles: { fillColor: [27, 1, 155] },
      });

      doc.save(`mes_presences_${new Date().toLocaleDateString().replace(/\//g, '-')}.pdf`);
    } catch (err) {
      console.error("Erreur lors de la génération du PDF", err);
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      padding: '30px',
      minHeight: '100vh',
      backgroundColor: '#f4f6f9',
      fontFamily: 'Segoe UI, sans-serif',
    },
    header: {
      color: '#1B019B',
      fontWeight: '600',
      fontSize: '1.5rem',
    },
    hr: {
      width: '80px',
      border: '2px solid #1B019B',
      opacity: '1',
      margin: '10px auto',
    },
    tableHeader: {
      backgroundColor: '#1B019B',
      color: '#fff',
    },
    tableCell: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    paginationButton: {
      backgroundColor: '#1B019B',
      color: 'white',
      border: 'none',
      margin: '0 2px',
      borderRadius: '5px',
      padding: '5px 10px',
    },
    paginationActive: {
      backgroundColor: '#0d0066',
      color: 'white',
    },
    paginationDisabled: {
      backgroundColor: '#e0e0e0',
      color: '#a0a0a0',
      cursor: 'not-allowed',
    },
    searchBar: {
      maxWidth: '300px',
      marginBottom: '15px',
      borderRadius: '20px',
      paddingLeft: '10px',
      height: '35px',
      border: '1px solid #ccc',
      fontSize: '1rem',
    },
    icon: {
      marginRight: '5px',
    },
    hoursSummary: {
      backgroundColor: '#f8f9fa',
      borderRadius: '8px',
      padding: '15px',
      marginBottom: '20px',
      display: 'flex',
      justifyContent: 'space-around',
      flexWrap: 'wrap',
      gap: '10px'
    },
    hoursItem: {
      textAlign: 'center',
      padding: '10px',
      minWidth: '120px',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
    },
    hoursValue: {
      fontSize: '1.2rem',
      fontWeight: '600',
      color: '#1B019B',
      marginTop: '5px'
    },
    rowHover: {
      cursor: 'pointer',
      transition: 'background-color 0.3s',
    },
    motifButton: {
      backgroundColor: 'transparent',
      border: 'none',
      color: '#1B019B',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '5px',
      fontSize: '0.9rem',
    },
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1050,
    },
    modalContent: {
      backgroundColor: 'white',
      padding: '20px',
      borderRadius: '8px',
      maxWidth: '500px',
      width: '90%',
      maxHeight: '80vh',
      overflowY: 'auto',
    },
  };

  return (
    <div>
      {/* Modal pour afficher le motif */}
      {motifModal.show && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={closeMotifModal}
        >
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" style={{ color: '#1B019B' }}>
                  Détail du motif
                </h5>
                <button className="btn-close" onClick={closeMotifModal}></button>
              </div>
              <div className="modal-body">
                <p
                  style={{
                    fontSize: '1rem',
                    textAlign: 'justify',
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                  }}
                >
                  {motifModal.motif}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={styles.container}>
        <div className="text-center mb-3">
          <h3 style={styles.header}>Mes Présences</h3>
          <hr style={styles.hr} />
        </div>

        <div className="card mb-4 shadow-sm" style={{ borderRadius: '15px', border: 'none' }}>
          <div className="card-body">
            <h5 className="card-title text-primary">Heures travaillées</h5>
            <div style={styles.hoursSummary}>
              <div style={styles.hoursItem}>
                <div><FaBusinessTime /> Aujourd'hui</div>
                <div style={styles.hoursValue}>{formatHeuresCompletes(hoursWorked.today)}</div>
                {selectedMonth && <small className="text-muted">Filtré par mois</small>}
              </div>
              <div style={styles.hoursItem}>
                <div><FaBusinessTime /> Cette semaine</div>
                <div style={styles.hoursValue}>{formatHeuresCompletes(hoursWorked.week)}</div>
                {selectedMonth && <small className="text-muted">Filtré par mois</small>}
              </div>
              <div style={styles.hoursItem}>
                <div><FaBusinessTime /> Ce mois</div>
                <div style={styles.hoursValue}>{formatHeuresCompletes(hoursWorked.month)}</div>
                {selectedMonth && <small className="text-muted">Filtré par mois</small>}
              </div>
              <div style={{ ...styles.hoursItem, border: '2px solid #dc3545', backgroundColor: '#f8d7da' }}>
                <div style={{ color: '#dc3545' }}>
                  <FaBriefcase style={{ marginRight: '5px' }} />
                  Pénalités
                </div>
                <div style={{ ...styles.hoursValue, color: '#dc3545' }}>
                  {montantPenalite.toLocaleString()} FCFA
                </div>
                {selectedMonth && <small className="text-muted">Filtré par mois</small>}
              </div>
            </div>
          </div>
        </div>

        <div className="card mb-4 shadow-sm" style={{ borderRadius: '15px', border: 'none' }}>
          <div className="card-body">
            <h5 className="card-title text-primary">Filtres</h5>
            <form className="row g-3">
             
              <div className="col-md-3">
                <label htmlFor="month" className="form-label">Mois</label>
                <select
                  id="month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="form-select"
                >
                  <option value="">Tous les mois</option>
                  {monthsList.map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-3">
                <label htmlFor="date" className="form-label">Date</label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={filters.date}
                  onChange={handleChange}
                  className="form-control"
                />
              </div>

              <div className="col-md-3">
                <label htmlFor="periode" className="form-label">Période</label>
                <select
                  id="periode"
                  name="periode"
                  value={filters.periode}
                  onChange={handleChange}
                  className="form-select"
                >
                  <option value="">Toutes les périodes</option>
                  {PERIODES_FRONT.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-3">
                <label htmlFor="statut" className="form-label">Statut</label>
                <select
                  id="statut"
                  name="statut"
                  value={filters.statut}
                  onChange={handleChange}
                  className="form-select"
                >
                  <option value="">Tous les statuts</option>
                  {Object.entries(STATUT_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-3">
                <label htmlFor="type_pointage" className="form-label">Type</label>
                <select
                  id="type_pointage"
                  name="type_pointage"
                  value={filters.type_pointage}
                  onChange={handleChange}
                  className="form-select"
                >
                  <option value="">Tous les types</option>
                  {TYPES_POINTAGE.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
            </form>
          </div>
        </div>

        <div className="d-flex justify-content-end mb-3">
          <button 
            className="btn btn-danger" 
            onClick={handleExportPDF}
            disabled={loading}
          >
            <FaDownload style={styles.icon} /> 
            {loading ? 'Génération du PDF...' : 'Exporter PDF'}
          </button>
        </div>

        <div className="card shadow-sm mb-4" style={{ borderRadius: '15px', border: 'none' }}>
          <div className="card-body table-responsive p-0">
            <table className="table table-hover text-center align-middle m-0">
              <thead>
                <tr style={styles.tableHeader}>
                  <th>Date</th>
                  <th>Heure</th>
                  <th>Type</th>
                  <th>Période</th>
                  <th>Statut</th>
                  <th>Justifié</th> 
                  <th>Motif</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Chargement...</span>
                      </div>
                    </td>
                  </tr>
                ) : presences.length > 0 ? (
                  presences.map((p) => (
                    <tr 
                      key={p.id} 
                      style={styles.rowHover}
                      className="hover-row"
                    >
                      <td>{p.date}</td>
                      <td>{formatHeure(p.heure)}</td>
                      <td style={styles.tableCell}>
                        {p.type_pointage === 'entree' ? (
                          <>
                            <FaSignInAlt style={{ marginRight: '8px', color: '#28a745' }} />
                            Entrée
                          </>
                        ) : p.type_pointage === 'sortie' ? (
                          <>
                            <FaSignOutAlt style={{ marginRight: '8px', color: '#dc3545' }} />
                            Sortie
                          </>
                        ) : null}
                      </td>
                      <td>
                        {p.periode === "autre" ? (
                          <span
                            title={p.motif_sortie || 'Sortie hors période normale'}
                            className="text-warning fw-bold d-inline-flex align-items-center gap-1"
                          >
                            <FiAlertTriangle /> Exceptionnelle
                          </span>
                        ) : p.periode === "matin" ? (
                          <span className="text-primary fw-bold d-inline-flex align-items-center gap-1">
                            <FiSunrise /> Matin
                          </span>
                        ) : p.periode === "apres_midi" ? (
                          <span className="text-success fw-bold d-inline-flex align-items-center gap-1">
                            <FiSunset /> Après-midi
                          </span>
                        ) : p.periode === "descente" ? (
                          <span className="text-danger fw-bold d-inline-flex align-items-center gap-1">
                            <FiArrowDownCircle /> Descente
                          </span>
                        ) : p.periode === "pause" ? (
                          <span className="text-info fw-bold d-inline-flex align-items-center gap-1">
                            <FiCoffee /> Pause
                          </span>
                        ) : (
                          p.periode?.charAt(0).toUpperCase() + p.periode?.slice(1).replace('_', ' ') || '-'
                        )}
                      </td>
                      <td>
                        {p.statut ? (
                          <span className={`badge ${p.statut === "PRESENT" ? 'bg-success' : p.statut === "RETARD" ? 'bg-warning' : 'bg-danger'} rounded-pill`}>
                            {STATUT_LABELS[p.statut] || p.statut}
                          </span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>
                        {/* Affichage de la justification */}
                        {p.statut === "RETARD" || p.statut === "ABSENT" ? (
                          <span className={`badge rounded-pill ${p.est_justifiee ? 'bg-success' : 'bg-danger'}`}>
                            {p.est_justifiee ? "Oui" : "Non"}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>
                        {p.motif_sortie ? (
                          <button
                            onClick={() => openMotifModal(p.motif_sortie)}
                            style={styles.motifButton}
                            className="btn btn-sm btn-outline-primary"
                          >
                            <FaInfoCircle /> Détail
                          </button>
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-4">
                      <div className="alert alert-info mb-0">
                        Aucune présence trouvée avec les critères sélectionnés
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 1 && (
            <div className="card-footer d-flex justify-content-center mt-3 bg-transparent border-top-0">
              <nav>
                <ul className="pagination mb-0">
                  <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(1)}
                      disabled={loading || page === 1}
                      style={page === 1 ? styles.paginationDisabled : styles.paginationButton}
                    >
                      Première
                    </button>
                  </li>
                  <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(page - 1)}
                      disabled={loading || page === 1}
                      style={page === 1 ? styles.paginationDisabled : styles.paginationButton}
                    >
                      &laquo;
                    </button>
                  </li>

                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (page <= 3) {
                      pageNum = i + 1;
                    } else if (page >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }

                    return (
                      <li key={pageNum} className={`page-item ${page === pageNum ? 'active' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(pageNum)}
                          disabled={loading}
                          style={page === pageNum ? { ...styles.paginationButton, ...styles.paginationActive } : styles.paginationButton}
                        >
                          {pageNum}
                        </button>
                      </li>
                    );
                  })}

                  <li className={`page-item ${page === pagination.totalPages ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(page + 1)}
                      disabled={loading || page === pagination.totalPages}
                      style={page === pagination.totalPages ? styles.paginationDisabled : styles.paginationButton}
                    >
                      &raquo;
                    </button>
                  </li>
                  <li className={`page-item ${page === pagination.totalPages ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(pagination.totalPages)}
                      disabled={loading || page === pagination.totalPages}
                      style={page === pagination.totalPages ? styles.paginationDisabled : styles.paginationButton}
                    >
                      Dernière
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </div>
      </div>
     
    </div>
  );
};

export default MesPresences;